import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, CalendarDays, Clock, Store, Truck, LeafyGreen, Navigation, X, Check, ExternalLink, User } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, CreditChip } from "@/components/site/primitives";
import { MARKETS, MARKET_TYPES, REGIONS } from "@/content/whereToBuy";
import { cn } from "@/lib/utils";

const typeIcon = {
  "Farmers' Market": Store,
  "CSA Pickup": LeafyGreen,
  "Wholesale": Truck,
};

const mapsUrl = (m) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.address || m.name)}`;

/* ---------------- Detail dialog (with farm photo) ---------------- */
const MarketDetail = ({ market, open, onOpenChange }) => {
  if (!market) return null;
  const Icon = typeIcon[market.type] || Store;
  const isInternal = market.website && market.website.startsWith("/");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="wtb-market-dialog"
        className="max-h-[92vh] max-w-lg gap-0 overflow-y-auto border-[var(--wf-border)] bg-[var(--wf-paper)] p-0 [&>button]:hidden rounded-sm"
      >
        {/* Farm photo */}
        <figure className="relative aspect-[16/9] w-full overflow-hidden bg-[var(--wf-cream)]">
          <img
            src={market.image}
            alt={market.imageAlt || `${market.name} — World Farmers growers`}
            data-testid="wtb-market-photo"
            className="h-full w-full object-cover"
          />
          <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
          <span className="absolute left-4 bottom-4 inline-flex items-center gap-1.5 rounded-full bg-[rgba(255,253,248,0.92)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--wf-green-text)] backdrop-blur-sm">
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {market.type}
          </span>
          <CreditChip src={market.image} className="absolute right-4 bottom-4 rounded-full bg-black/45 px-2.5 py-1 text-[10px] font-medium text-white/90 backdrop-blur-sm" />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close details"
            data-testid="wtb-market-close-button"
            className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-ink)] shadow-[var(--wf-shadow-sm)] backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </figure>

        <div className="p-6">
          <DialogTitle className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">{market.name}</DialogTitle>
          <DialogDescription className="mt-1 text-sm leading-6 text-muted-foreground">{market.blurb}</DialogDescription>

          <ul className="mt-4 space-y-2.5 text-sm text-[var(--wf-ink)]">
            {market.farmer && (
              <li className="flex items-start gap-3" data-testid="wtb-market-farmer">
                <User className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-maroon)]" aria-hidden="true" />
                <span>{market.farmer}{market.farm ? ` · ${market.farm}` : ""}</span>
              </li>
            )}
            <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-maroon)]" aria-hidden="true" />{market.address}</li>
            <li className="flex items-start gap-3"><CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-maroon)]" aria-hidden="true" /><span>{market.days} · {market.season}</span></li>
            <li className="flex items-center gap-3"><Clock className="h-4 w-4 shrink-0 text-[var(--wf-maroon)]" aria-hidden="true" />{market.hours}</li>
          </ul>

          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">What you'll find</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {market.sells.map((s) => (
                <span key={s} className="rounded-full bg-[var(--wf-wheat)] px-3 py-1 text-xs font-medium text-[var(--wf-maroon)]">{s}</span>
              ))}
            </div>
          </div>

          {market.snapHip && (
            <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[rgba(47,93,58,0.10)] px-3 py-2 text-xs font-semibold text-[var(--wf-green-text)]">
              <Check className="h-4 w-4" aria-hidden="true" />
              SNAP &amp; HIP benefits accepted
            </p>
          )}

          {market.placeholder && (
            <p className="mt-4 text-xs text-muted-foreground" data-testid="wtb-market-placeholder-note">Details to be confirmed.</p>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {market.type !== "Wholesale" && (
              <a
                href={mapsUrl(market)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wtb-market-directions-link"
                className="inline-flex min-h-11 items-center gap-2 rounded-sm bg-[var(--wf-maroon)] px-5 py-3 text-sm font-semibold text-[var(--wf-paper)] transition-colors hover:bg-[var(--wf-maroon-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                <Navigation className="h-4 w-4" aria-hidden="true" />
                Get directions
              </a>
            )}
            {market.website && (isInternal ? (
              <Link
                to={market.website}
                data-testid="wtb-market-website-link"
                className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-[var(--wf-maroon)] px-5 py-3 text-sm font-semibold text-[var(--wf-maroon)] transition-colors hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                {market.type === "CSA Pickup" ? "Sign up for the CSA" : "Learn more"}
              </Link>
            ) : (
              <a
                href={market.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wtb-market-website-link"
                className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-[var(--wf-maroon)] px-5 py-3 text-sm font-semibold text-[var(--wf-maroon)] transition-colors hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                Market website <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Finder ---------------- */
/**
 * MarketFinder — searchable, filterable grid of buying locations.
 * @param items      subset of MARKETS (default: all)
 * @param types      type options for the filter (default: MARKET_TYPES)
 * @param showTypeFilter  hide the type select when a page is pre-filtered
 */
export const MarketFinder = ({
  id = "markets",
  items = MARKETS,
  types = MARKET_TYPES,
  showTypeFilter = true,
  eyebrow = "Find a market",
  title = "Markets & CSA pickups",
  intro = "Search by name, town, farmer, or crop, then filter by type and region to find fresh produce near you.",
  testidPrefix = "wtb",
  className = "bg-white",
}) => {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("All");
  const [region, setRegion] = useState("All");
  const [selected, setSelected] = useState(null);

  const regions = useMemo(() => REGIONS.filter((r) => items.some((m) => m.region === r)), [items]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((m) => {
      const matchesType = type === "All" || m.type === type;
      const matchesRegion = region === "All" || m.region === region;
      const matchesQuery =
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.town.toLowerCase().includes(q) ||
        (m.farmer && m.farmer.toLowerCase().includes(q)) ||
        (m.farm && m.farm.toLowerCase().includes(q)) ||
        m.sells.some((s) => s.toLowerCase().includes(q));
      return matchesType && matchesRegion && matchesQuery;
    });
  }, [items, query, type, region]);

  return (
    <section id={id} aria-labelledby={`${id}-heading`} className={cn("wf-section scroll-mt-20", className)}>
      <div className="wf-container">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">{eyebrow}</span>
          <h2 id={`${id}-heading`} className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">{title}</h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">{intro}</p>
        </Reveal>

        {/* Controls */}
        <div className="mx-auto mt-10 flex max-w-4xl flex-col gap-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <label htmlFor={`${id}-search`} className="sr-only">Search locations</label>
            <input
              id={`${id}-search`}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by market, town, farmer, or crop…"
              data-testid={`${testidPrefix}-search-input`}
              className="min-h-11 w-full rounded-lg border border-[var(--wf-border)] bg-[var(--wf-paper)] py-2.5 pl-9 pr-3 text-sm text-[var(--wf-ink)] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            {showTypeFilter && (
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="min-h-11 w-full border-[var(--wf-border)] bg-[var(--wf-paper)] sm:w-[180px]" data-testid={`${testidPrefix}-type-select`} aria-label="Filter by type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All types</SelectItem>
                  {types.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="min-h-11 w-full border-[var(--wf-border)] bg-[var(--wf-paper)] sm:w-[220px]" data-testid={`${testidPrefix}-region-select`} aria-label="Filter by region"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All regions</SelectItem>
                {regions.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="mx-auto mt-4 max-w-4xl text-sm text-muted-foreground" data-testid={`${testidPrefix}-results-count`} aria-live="polite">
          {results.length} {results.length === 1 ? "location" : "locations"} found
        </p>

        {/* Results */}
        {results.length === 0 ? (
          <div data-testid={`${testidPrefix}-empty-state`} className="mx-auto mt-6 max-w-md rounded-sm border border-dashed border-[var(--wf-border)] bg-[var(--wf-cream)] p-10 text-center">
            <LeafyGreen className="mx-auto h-8 w-8 text-[var(--wf-moss)]" aria-hidden="true" />
            <p className="mt-3 font-serif text-lg font-semibold text-[var(--wf-ink)]">No locations match your search</p>
            <p className="mt-1 text-sm text-muted-foreground">Try clearing the filters or searching a different crop.</p>
            <button type="button" onClick={() => { setQuery(""); setType("All"); setRegion("All"); }} data-testid={`${testidPrefix}-clear-filters-button`} className="mt-4 inline-flex min-h-11 items-center rounded-full bg-[var(--wf-maroon)] px-4 py-2 text-xs font-semibold text-[var(--wf-paper)] hover:bg-[var(--wf-maroon-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Clear filters</button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((m, i) => {
              const Icon = typeIcon[m.type] || Store;
              return (
                <Reveal key={m.id} delay={0.04 * (i % 3)}>
                  <button
                    type="button"
                    onClick={() => setSelected(m)}
                    data-testid={`${testidPrefix}-market-card-${m.id}`}
                    className="group flex h-full w-full flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] text-left shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                  >
                    <div className="relative aspect-[16/9] w-full overflow-hidden bg-[var(--wf-cream)]">
                      <img src={m.image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out motion-safe:group-hover:scale-105" />
                      <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-[rgba(255,253,248,0.92)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--wf-green-text)] backdrop-blur-sm">
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        {m.type}
                      </span>
                      {m.snapHip && <span className="absolute right-4 top-4 rounded-full bg-[var(--wf-wheat)] px-2.5 py-0.5 text-[10px] font-semibold text-[var(--wf-maroon)]">SNAP / HIP</span>}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-serif text-xl font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">{m.name}</h3>
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" aria-hidden="true" />{m.town}</p>
                      {m.farmer && <p className="mt-1 flex items-center gap-1.5 text-sm text-[var(--wf-soil)]"><User className="h-3.5 w-3.5 text-[var(--wf-green-text)]" aria-hidden="true" />{m.farmer}{m.farm ? ` · ${m.farm}` : ""}</p>}
                      <div className="mt-4 flex flex-1 flex-col justify-end">
                        <p className="flex items-center gap-1.5 text-sm text-[var(--wf-ink)]"><CalendarDays className="h-3.5 w-3.5 text-[var(--wf-green-text)]" aria-hidden="true" />{m.days}{m.hours && m.type === "Farmers' Market" ? ` · ${m.hours}` : ""}</p>
                        <p className="mt-1 text-xs uppercase tracking-[0.1em] text-muted-foreground">Season · {m.season}</p>
                        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--wf-maroon)]">View details →</span>
                      </div>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      <MarketDetail market={selected} open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }} />
    </section>
  );
};
